import { useCallback, useState } from "react";
import { trpc } from "../../_core/trpc";
const LIMITS = {
  photo: {
    maxBytes: 10 * 1024 * 1024,
    types: ["image/jpeg", "image/png", "image/webp", "image/avif", "image/gif"],
    label: "JPEG, PNG, WebP, AVIF or GIF up to 10 MB"
  },
  video: {
    maxBytes: 100 * 1024 * 1024,
    types: ["video/mp4", "video/webm"],
    label: "MP4 or WebM up to 100 MB"
  },
  audio: {
    maxBytes: 20 * 1024 * 1024,
    types: ["audio/mpeg", "audio/wav"],
    label: "MP3 or WAV up to 20 MB"
  }
};
async function downscaleImage(file, maxEdge = 2400) {
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file).catch(async () => {
      const url = URL.createObjectURL(file);
      try {
        const img = await new Promise((resolve, reject) => {
          const el = new Image();
          el.onload = () => resolve(el);
          el.onerror = () => reject(new Error("decode failed"));
          el.src = url;
        });
        return img;
      } finally {
        URL.revokeObjectURL(url);
      }
    });
    const w = "width" in bitmap ? bitmap.width : 0;
    const h = "height" in bitmap ? bitmap.height : 0;
    if (!w || !h) return file;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    if (scale >= 0.95 && file.size < 1.5 * 1024 * 1024) return file;
    const outW = Math.round(w * scale);
    const outH = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, outW, outH);
    if ("close" in bitmap && typeof bitmap.close === "function") bitmap.close();
    const blob = await new Promise(
      (resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) return file;
    if (blob.size >= file.size) return file;
    const base = file.name.replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}
async function probeDuration(file) {
  if (!file.type.startsWith("video/") && !file.type.startsWith("audio/")) return void 0;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(file.type.startsWith("video/") ? "video" : "audio");
    const done = (v) => {
      URL.revokeObjectURL(url);
      resolve(v);
    };
    const timer = window.setTimeout(() => done(void 0), 6e3);
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      window.clearTimeout(timer);
      const d = el.duration;
      done(Number.isFinite(d) && d > 0 ? Math.round(d) : void 0);
    };
    el.onerror = () => {
      window.clearTimeout(timer);
      done(void 0);
    };
    el.src = url;
  });
}
async function grabPoster(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    const cleanup = (f) => {
      URL.revokeObjectURL(url);
      resolve(f);
    };
    const timer = window.setTimeout(() => cleanup(null), 7e3);
    video.onloadeddata = () => {
      video.currentTime = Math.min(0.6, (video.duration || 1) * 0.12);
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        const w = video.videoWidth || 720;
        const h = video.videoHeight || 1280;
        const scale = Math.min(1, 900 / Math.max(w, h));
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) return cleanup(null);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            window.clearTimeout(timer);
            if (!blob) return cleanup(null);
            cleanup(new File([blob], "poster.jpg", { type: "image/jpeg" }));
          },
          "image/jpeg",
          0.85
        );
      } catch {
        window.clearTimeout(timer);
        cleanup(null);
      }
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      cleanup(null);
    };
    video.src = url;
  });
}
function fileToBase64(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) {
        onProgress(Math.round(ev.loaded / ev.total * 100));
      }
    };
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      onProgress?.(100);
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("Could not read that file. Please try again."));
    reader.readAsDataURL(file);
  });
}
function useUploader(kind) {
  const utils = trpc.useUtils();
  const uploadViaServer = trpc.admin.uploadViaServer.useMutation();
  const [status, setStatus] = useState({ state: "idle" });
  const uploadOne = useCallback(
    async (file, opts = {}) => {
      const limit = LIMITS[kind];
      const prepped = kind === "photo" ? await downscaleImage(file) : file;
      if (!limit.types.includes(prepped.type)) {
        const msg = `That file is ${prepped.type || "an unknown type"}. Please use ${limit.label}.`;
        setStatus({ state: "error", name: file.name, message: msg });
        return { ok: false, message: msg };
      }
      if (prepped.size > limit.maxBytes) {
        const msg = `${(prepped.size / 1024 / 1024).toFixed(1)} MB is over the ${(limit.maxBytes / 1024 / 1024).toFixed(0)} MB limit for ${kind}. ${kind === "video" ? "Compress it first, or trim it shorter." : "Please choose a smaller file."}`;
        setStatus({ state: "error", name: file.name, message: msg });
        return { ok: false, message: msg };
      }
      if (prepped !== file) {
        setStatus({ state: "preparing", name: file.name, pct: 0 });
      }
      let duration;
      if (kind === "video" || kind === "audio") {
        duration = await probeDuration(prepped);
      }
      let posterBase64;
      let posterType;
      if (kind === "video") {
        const poster = await grabPoster(prepped);
        if (poster) {
          posterBase64 = await fileToBase64(poster);
          posterType = poster.type;
        }
      }
      try {
        setStatus({ state: "uploading", name: file.name, pct: 5 });
        const dataBase64 = await fileToBase64(
          prepped,
          (pct) => setStatus({ state: "uploading", name: file.name, pct: Math.max(5, pct) })
        );
        await uploadViaServer.mutateAsync({
          kind,
          name: prepped.name,
          contentType: prepped.type,
          dataBase64,
          caption: opts.caption || void 0,
          durationSec: duration,
          posterBase64,
          posterType,
          visibleBeforeUnlock: opts.visibleBeforeUnlock
        });
        setStatus({ state: "done", name: file.name });
        await utils.admin.listMedia.invalidate();
        await utils.admin.overview.invalidate();
        await utils.public.state.invalidate();
        return { ok: true };
      } catch (e) {
        const msg = e instanceof Error ? e.message : "The upload failed. Please try again.";
        setStatus({ state: "error", name: file.name, message: msg });
        return { ok: false, message: msg };
      }
    },
    [kind, uploadViaServer, utils]
  );
  const reset = useCallback(() => setStatus({ state: "idle" }), []);
  return { uploadOne, status, reset, busy: status.state === "uploading" || status.state === "preparing" };
}
export {
  LIMITS,
  downscaleImage,
  fileToBase64,
  grabPoster,
  useUploader
};
